// Bescherming tegen stil verlies van niet-opgeslagen wijzigingen.
//
// WAAROM GEEN useBlocker
// ----------------------
// De app draait op <BrowserRouter>, geen data router: useBlocker van react-router werkt
// daar niet. Daarom twee lagen:
//   1. beforeunload — venster sluiten, herladen of een externe URL. De browser toont
//      zijn eigen (niet te stijlen) vraag.
//   2. een lichte registry — de shell-navigatie (zijbalk, logo) vraagt vóór het navigeren
//      `requestNavigation(proceed)`. Staat er ergens iets "dirty", dan verschijnt eerst
//      een ConfirmDialog; anders loopt `proceed` meteen.
//
// Een scherm meldt zich aan met `useUnsavedChangesGuard(dirty)`. De SaveBar doet dat al
// voor elke editor die de save-bar gebruikt, dus die schermen hoeven niets extra te doen.

import { createContext, useContext, useEffect, useId, useMemo } from "react";

// De provider (met de bevestigingsdialoog) staat in components/layout/UnsavedChangesProvider.jsx
// en wordt door de AppShell geplaatst.
export const UnsavedChangesContext = createContext(null);

// Voor de shell: vraag bevestiging vóór een navigatie. Buiten een provider (bv. een
// componenttest) loopt de navigatie gewoon door.
export function useNavigationGuard() {
  const ctx = useContext(UnsavedChangesContext);
  return useMemo(
    () =>
      ctx ?? {
        isDirty: () => false,
        requestNavigation: (proceed) => proceed(),
      },
    [ctx]
  );
}

export default function useUnsavedChangesGuard(dirty) {
  const ctx = useContext(UnsavedChangesContext);
  const setDirty = ctx?.setDirty;
  const id = useId();
  const isDirty = !!dirty;

  useEffect(() => {
    if (!setDirty) return undefined;
    setDirty(id, isDirty);
    return () => setDirty(id, false);
  }, [setDirty, id, isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      // Oudere browsers tonen de vraag alleen met een returnValue.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);
}
